import { z } from 'zod';
import { HexColour, IsoDateTime, Locale, PageQuery, TimeZone, Uuid, page } from './common.js';
/**
 * Sites module (docs/modules/sites.md). Owner: Phase 1 task S1.
 * Builders may add optional fields; renaming or removing a field needs the architect.
 */
export const Address = z.object({
    line1: z.string().trim().max(120).default(''),
    line2: z.string().trim().max(120).default(''),
    town: z.string().trim().max(80).default(''),
    county: z.string().trim().max(80).default(''),
    postcode: z.string().trim().max(16).default(''),
    /** ISO 3166-1 alpha-2. */
    country: z.string().regex(/^[A-Z]{2}$/).default('GB'),
});
export const SiteStatus = z.enum(['active', 'archived']);
export const Site = z.object({
    id: Uuid,
    name: z.string(),
    address: Address,
    timeZone: TimeZone,
    language: Locale,
    /** Used as Reply-To on this site's emails (Phase 2 pre-registration). Empty = tenant default. */
    replyToName: z.string(),
    replyToEmail: z.string(),
    /** The optional "Remote site" (Phase 3). Always false in Phase 1. */
    isRemote: z.boolean(),
    status: SiteStatus,
    /** 'all' = every group shows on this site; 'selected' = only groups in site_groups with visible = true. */
    groupVisibility: z.enum(['all', 'selected']),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const SiteCreate = z.object({
    name: z.string().trim().min(1).max(80),
    address: Address.partial().optional(),
    /** Default: tenant core setting locale.timeZone. */
    timeZone: TimeZone.optional(),
    language: Locale.optional(),
    replyToName: z.string().trim().max(80).optional(),
    replyToEmail: z.union([z.literal(''), z.email()]).optional(),
});
export const SiteUpdate = SiteCreate.partial().extend({ status: SiteStatus.optional() });
export const SiteListQuery = PageQuery.extend({
    status: z.enum(['active', 'archived', 'all']).default('active'),
});
/** PUT /sites/:id/groups. Groups hidden by this save have their open visits signed out (event sites.site.groups_changed). */
export const SiteGroupsPut = z.object({
    mode: z.enum(['all', 'selected']),
    /** Visible groups when mode = 'selected'. Ignored for 'all'. */
    groupIds: z.array(Uuid).max(200).default([]),
});
export const SiteGroups = z.object({
    mode: z.enum(['all', 'selected']),
    groupIds: z.array(Uuid),
});
/** Site-level settings of the `sites` module (scoped settings, ADR 0013). 0 / false switches a behaviour off. */
export const SitesSiteSettings = z.object({
    kioskBranding: z
        .object({
        /** files.id, JPEG/PNG up to 2 MB, ideally 2048 x 1536. */
        welcomeImageFileId: Uuid.nullable().default(null),
        /** files.id; null = tenant logo. */
        logoFileId: Uuid.nullable().default(null),
        backgroundColour: HexColour.default('#FFFFFF'),
        textColour: HexColour.default('#0F172A'),
        /** Empty = the i18n default ("Tap the screen to sign in or out"). */
        welcomeText: z.string().max(120).default(''),
        showClock: z.boolean().default(true),
    })
        .default({
        welcomeImageFileId: null,
        logoFileId: null,
        backgroundColour: '#FFFFFF',
        textColour: '#0F172A',
        welcomeText: '',
        showClock: true,
    }),
    privacy: z
        .object({
        /** Suggest names of returning standard-group visitors as they type (online only). */
        autocompleteReturningVisitors: z.boolean().default(true),
        /** Phase 2 (pre-registration): only show pre-registered names on an exact match. Stored now, used later. */
        exactMatchPreRegisteredOnly: z.boolean().default(false),
        /** The kiosk sign-out list hides standard-group visitors' names; they search or scan their badge instead. */
        hideVisitorsOnSignOutList: z.boolean().default(false),
        /** Standard-group visitors must enter at least two words. */
        requireFullName: z.boolean().default(false),
        hideProductLogoOnThankYou: z.boolean().default(false),
    })
        .default({
        autocompleteReturningVisitors: true,
        exactMatchPreRegisteredOnly: false,
        hideVisitorsOnSignOutList: false,
        requireFullName: false,
        hideProductLogoOnThankYou: false,
    }),
    features: z
        .object({
        /** Kiosk camera scans badge and member QR codes. */
        qrScanning: z.boolean().default(true),
        /** Master switch for kiosk photos at this site; each group's photo mode applies only when this is on. */
        visitorPhotos: z.boolean().default(true),
    })
        .default({ qrScanning: true, visitorPhotos: true }),
});
/** Tenant-level settings of the `sites` module. */
export const SitesTenantSettings = z.object({
    /** Maximum number of active sites (0 = no limit). Lets billing (Phase 4) cap plans. */
    maxSites: z.number().int().min(0).default(0),
});
/** Short form used in pickers and the portal's site selector. */
export const SiteSummary = z.object({ id: Uuid, name: z.string(), timeZone: TimeZone, status: SiteStatus });
/**
 * GET /sites for a user without `sites.view` (the portal's site picker): id and name only, active sites the user may see.
 * Added by S1 for the portal's top bar.
 */
export const SiteMini = z.object({ id: Uuid, name: z.string() });
/** GET /sites answers with full `Site` objects for `sites.view`, otherwise with `SiteMini` objects. */
export const SiteListResponse = page(z.union([Site, SiteMini]));
/** GET /sites/:id/settings and GET /groups/:id/settings: the enabled modules that have settings at that level. */
export const ScopedSettingsModule = z.object({ moduleKey: z.string(), nameKey: z.string() });
//# sourceMappingURL=sites.js.map